<template>
  <div>
    <b-row>
      <b-col cols="12" lg="6">
        <loader
          v-if="invoicesLoader"
          color="grey"
        />

        <div class="d-flex justify-content-between align-items-center mb-3">
          <h2 class="text-uppercase mb-0 billing-title">
            {{ $t('billing.invoices') }}
          </h2>
          <multiselect
            v-if="!invoicesLoader && options.length"
            v-model="selectedPeriod"
            class="w-50 ml-4"
            label="title"
            track-by="title"
            :preselect-first="true"
            placeholder="Select Period"
            :options="options"
            :searchable="false"
            :allow-empty="false"
            :close-on-select="true"
            select-label=""
            selected-label=""
            deselect-label=""
          />
        </div>

        <div v-for="(item, index) in invoicesInfoData" :key="index">
          <div class="white-container">
            <InvoicesInfo
              :total="item.total"
              :label="item.label"
              :is-paid="item.isPaid"
              :invoice-pdf-url="item.invoicePdf"
              :invoice-number="item.invoiceNumber"
              :items="item.items"
              :id="item.id"
              :stripe-checkout="!cardLength"
              :checkout-url="item.checkoutUrl"
              :payment-due-date="item.paymentDueDate"
              @paid="item.isPaid = true"
            />
          </div>
        </div>

        <div
          v-if="!invoicesInfoData.length && !invoicesLoader"
          class="text-muted text-center"
        >
          <div class="white-container py-5">
            {{ $t('global.no_data') }}
          </div>
        </div>
      </b-col>
      <b-col cols="12" lg="6">
        <b-row>
          <b-col cols="12">
            <div class="white-container">
              <h5
                  class="font-weight-light text-uppercase transactions-history-title"
                  :class="{ empty: isTransactionHistoryEmpty }"
              >
                {{ $t('billing.transaction_history') }}
              </h5>

              <BillingTransactionHistory
                  :resource-key="$store.getters['resource/currentResource'].key"
                  @table-empty="isTransactionHistoryEmpty = $event"
              />
            </div>
          </b-col>

          <b-col cols="12">
            <div class="white-container" style="min-height: 200px">
              <loader v-if="!billingFormLoaded" />

              <div
                v-else-if="billingFormError && billingFormLoaded"
                class="d-flex align-items-center justify-content-center"
                style="height: 200px"
              >
                <h5 class="billing-form-error" v-text="billingFormError" />
              </div>

              <div v-else-if="billingFormLoaded">
                <template v-if="stripePaymentInLive">
                  <h5 class="font-weight-light text-uppercase">
                    {{ $t('billing.info') }}
                  </h5>
                  <EditBillingEmail
                    mode="live"
                    :email-object="customerEmail"
                    :resource="$store.getters['resource/currentResource'].key"
                    @email-changed="fetchCardInfo"
                  />
                  <CardForm
                    :cards="cards.live"
                    :customer-email="customerEmail.live"
                    :card-loading="cardLoading"
                    mode="live"
                    @card-add="handleCardChange"
                    @card-remove="handleCardRemove"
                  />
                </template>
                <template v-else>
                  <h4 class="mb-4 text-uppercase">
                    Test {{ $t('billing.info') }}
                  </h4>
                  <EditBillingEmail
                    mode="test"
                    :email-object="customerEmail"
                    :resource="$store.getters['resource/currentResource'].key"
                    @email-changed="fetchCardInfo"
                  />
                  <CardForm
                    :cards="cards.test"
                    :card-loading="cardLoading"
                    :customer-email="customerEmail.test"
                    mode="test"
                    @card-add="handleCardChange"
                    @card-remove="handleCardRemove"
                  />
                </template>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import InvoicesInfo from "@/views/billing/InvoicesInfo";
import CardForm from "@/views/billing/CardForm";
import EditBillingEmail from "@/components/billing/card/EditBillingEmail";
import moment from "moment";
import BillingTransactionHistory from "./BillingTransactionHistory";
import InvoicesMixin from "../../mixins/invoices";

export default {
  name: 'BillingInfoView',
  mixins: [InvoicesMixin],
  components: {
    CardForm,
    InvoicesInfo,
    EditBillingEmail,
    BillingTransactionHistory
  },
  data () {
    return {
      customerEmail: {
        test: '',
        live: ''
      },
      isTransactionHistoryEmpty: false,
      invoicesLoader: false,
      cardLoading: true,
      transactionHistoryLoader: false,
      invoicesInfoData: [],
      options: [],
      selectedPeriod: '',
      cards: {
        live: [],
        test: []
      },
      transactionHistory: [],
      transactionHistoryLoaded: false,
      stripePaymentInLive: false,
      billingFormLoaded: false,
      billingFormError: ''
    }
  },
  async created() {
    this.invoicesLoader = true;
    this.fetchCardInfo();
    await this.fetchInvoicesPeriods();
    await this.fetchPricingInvoices();
    await this.fetchPricingSettings();
    this.invoicesLoader = false;
  },
  methods: {
    async fetchCardInfo() {
      try {
        this.cardLoading = true;
        await this.getCardInfo('test').then((card) => this.addNewCard(card, 'test'));
        await this.getCardInfo('live').then((card) => this.addNewCard(card, 'live'));
        this.cardLoading = false;
      } catch (error) {
        this.cardLoading = false;
      }
    },
    handleCardChange(card) {
      if (!card.livemode) {
        this.addNewCard(card, 'test');
      } else {
        this.addNewCard(card, 'live');
      }
    },
    handleCardRemove(mode) {
      if (mode === 'live') {
        this.cards['live'] = [];
      } else {
        this.cards['test'] = [];
      }
    },
    addNewCard(card, mode) {
      if (card && mode) {
        this.cards[mode] = [card];
      }
    },
    getCardInfo(mode) {
      return this.getData(`resources/${this.$store.getters['resource/currentResource'].key}/pricing/payment-data/stripe/${mode}`, null, false)
          .then(({ data }) => {
            if (data.customer_email) {
              this.customerEmail[mode] = data.customer_email;
            }

            if (data.card_last4) {
              return {
                card_brand: data.card_brand,
                card_last4: data.card_last4,
                customer_email: data.customer_email,
                expiration_date: data.expiration_date,
                id: data.id,
                livemode: data.livemode,
                stripe_card_id: data.stripe_card_id,
                stripe_customer_id: data.stripe_customer_id,
                stripe_payment_method_billing_details: data.stripe_payment_method_billing_details,
                stripe_payment_method_id: data.stripe_payment_method_id,
                resource_id: data.resource_id
              }
            } else {
              return null;
            }
          }).catch((error) => {
            if (error && error.response) {
              this.$noty.error(error.response.data.error_description);
            }
          })
    },
    fetchPricingSettings() {
      return this.getData(`resources/${this.$store.getters['resource/currentResource'].key}/pricing`, null, false, false, false, true)
          .then(({ data }) => {
            if (data) {
              this.stripePaymentInLive = !!data.livemode;
              this.billingFormLoaded = true;
            }
          }).catch((error) => {
            if (error && error.response) {
              this.billingFormError = error.response.data.error_description;
            }
          }).finally(() => this.billingFormLoaded = true);
    },
    fetchInvoicesPeriods () {
      return this.getData(`resources/${this.$store.getters['resource/currentResource'].key}/pricing/invoices/periods`, null, false)
          .then(({data}) => {
            if (Array.isArray(data.items)){
              this.options = data.items.map((dateStr) => {
                return {
                  title: moment(dateStr).format('MMMM YYYY')
                }
              });

              this.selectedPeriod = this.options[0] || null;
            }
          }).catch((error) => {
            if (error && error.response) {
              this.$noty.error(error.response.data.error_description);
            }
          });
    },
    fetchPricingInvoices () {
      this.invoicesLoader = true;
      return this.getData(`resources/${this.$store.getters['resource/currentResource'].key}/pricing/invoices`, {
        params: {
          limit: 10,
          page: 1,
          calendar_period: this.selectedPeriodStart,
        }
      }, false)
          .then(({data}) => {
            this.invoicesInfoData = data.items.map((item) => {
              return this.formatInvoiceObject(item);
            })
          }).catch((error) => {
            if (error && error.response) {
              this.$noty.error(error.response.data.error_description);
            }
          }).finally(() => {
            this.invoicesLoader = false;
          })
    },
  },
  computed: {
    selectedPeriodStart () {
      return this.selectedPeriod ? moment(this.selectedPeriod.title).format('YYYY-MM') : '';
    },
    isRoot() {
      return this.$store.getters['auth/user'].role === 'root';
    },
    stripePaymentEnvMode() {
      return process.env.VUE_APP_STRIPE_MODE || 'live';
    },
    cardLength() {
      return this.cards.live.length || this.cards.test.length;
    }
  },
  watch: {
    selectedPeriod: function (val) {
      this.fetchPricingInvoices();
    }
  }
}
</script>